import type { Address, InvoiceBillingStatus } from '@weft/types';
import { PAYMENT_TERMS, type PaymentTerm, type Period, type InvoicePaymentProviderInformation, INVOICE_BILLING_STATUSES } from '@weft/types';
import { isNotNullOrUndefined, isNullOrUndefined } from '@weft/utils';
import { formatNumber } from '@weft/utils';
import cn from 'classnames';
import dayjs from 'dayjs';
import React from 'react';
import { formatDate } from '../../../../utils/date';

type LightInvoiceHeaderProps = {
  invoiceId: string;
  purchaseOrder?: string;
  billingCycle: Period;
  customerName: string;
  totalPrice: number;
  commitment: number;
  companyName: string;
  isStatement?: boolean;
  isPdf?: boolean;
  paymentDueDays: number;
  currencySymbol: string;
  paymentTerm: PaymentTerm;
  billingStatus: InvoiceBillingStatus;
  paymentProviderInformation?: InvoicePaymentProviderInformation,
  customerAddress?: Address;
};

export const LightInvoiceHeader: React.FC<LightInvoiceHeaderProps> = ({
  invoiceId,
  customerName,
  totalPrice,
  commitment,
  paymentProviderInformation,
  companyName,
  billingStatus,
  purchaseOrder,
  billingCycle,
  isStatement,
  isPdf = true,
  currencySymbol,
  paymentDueDays,
  paymentTerm,
  customerAddress,
}) => {
  const issueDate = paymentTerm === PAYMENT_TERMS.Postpayment ? formatDate(dayjs(billingCycle.endTime).add(1, 'day')) : formatDate(dayjs(billingCycle.startTime));

  return (
    <div className={cn(isPdf ? 'pb-1' : 'pb-8')}>
      <div className={cn('border-line flex flex-wrap justify-between border-b',
        isPdf ? 'gap-2 pb-2' : 'gap-6 pb-4')}
      >
        {!isStatement && (
          <div className={cn('border-line flex w-full flex-wrap justify-between border-b',
            isPdf ? 'gap-2 pb-4' : 'gap-6 pb-8')}
          >

            <div className="flex flex-col gap-2">
              <div className='flex items-end gap-x-2 text-3xl font-medium'>
                <span className="flex">Invoice</span>
                <span className='text-offwhite mt-auto flex text-base font-normal uppercase'>{`#${getFormattedId(invoiceId)}`}</span>
              </div>

              <div className='flex items-center gap-1 text-sm'>
                <div className='text-offwhite font-normal'>Billed by</div>
                <div className='font-medium text-black'>{companyName}</div>
              </div>
            </div>
            <div className='flex justify-between gap-20'>
              <div className='flex flex-col'>
                <div className='text-offwhite ml-auto text-sm font-normal'>Amount Due</div>
                <div className='text-3xl font-medium text-black'>
                  <span className='text-xl font-normal'>{currencySymbol}</span>
                  {formatNumber(totalPrice)}
                </div>
                { isNotNullOrUndefined(paymentProviderInformation?.externalInvoicePaymentLink) 
                && billingStatus !== INVOICE_BILLING_STATUSES.Paid && (
                  <div>
                    <label className='text-style'>
                      <a
                        href={paymentProviderInformation?.externalInvoicePaymentLink}
                        target="_blank"
                      >
                        Pay Now
                      </a>
                    </label>
                  </div>
                ) }
              </div>
            </div>
          </div>
        )}
        <div className='w-full pb-4 font-normal'>
          <div className='text-label text-sm'>Bill to</div>
          <div className='flex justify-between'>
            <div>
              <div className='text-xl font-medium text-black'>{customerName}</div>
              <div className='text-label max-w-[15rem] text-sm'>{getParsedAddress(customerAddress)}</div>
            </div>
            {!isStatement && (
              <>            
                {' '}
                <div>
                  <div className='text-label text-sm'>PO</div>
                  <div className='text-lg text-black'>{purchaseOrder ?? '--'}</div>
                </div>
                {commitment ? (
                  <div>
                    <div className='text-label text-sm'>Commitment</div>
                    <div className='text-lg text-black'>{formatNumber(commitment)}</div>
                  </div>
                ) : <></>}
                <div>
                  <div className='text-label text-sm'>Issue date</div>
                  <div className='text-lg text-black'>{issueDate}</div>
                </div>
                <div>
                  <div className='text-label text-sm'>Due date</div>
                  <div className="text-lg text-black">{`${paymentDueDays} days`}</div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function getParsedAddress(address: Address | undefined) {
  if (isNullOrUndefined(address)) {
    return '';
  }

  return (
    <span>
      {address.addressText}
      {' '}
      # 
      {' '}
      {address.postalCode} 
      <br />
      {address.city}
      , 
      {' '}
      {address.country}
    </span>
  );
}

const getFormattedId = (id: string, charCount = 5) => {
  const digits = -Math.abs(charCount);

  return id.slice(digits);
};